import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Store,
  MapPin,
  Save,
  RotateCcw,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { fetchData } from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import PageHeader from '../components/layout/PageHeader';
import GlassmorphicCard from '../components/common/GlassmorphicCard';
import InputField from '../components/common/InputField';
import Button from '../components/common/Button';

const SettingsPage = () => {
  const { authToken, selectedShopCode } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [settings, setSettings] = useState({
    shop_name: '',
    shop_address_1: '',
    shop_address_2: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchData(
        'api/pos/v1/setting/all',
        'GET',
        null,
        authToken,
        selectedShopCode
      );
      if (response.status && response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      showError('Failed to load store settings.');
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedShopCode, showError]);

  const saveSettings = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      setSaving(true);
      const response = await fetchData(
        'api/pos/v1/setting/sync',
        'POST',
        { settings },
        authToken,
        selectedShopCode
      );

      if (response.status) {
        showSuccess('Store settings updated successfully.');
        setIsEditing(false);
      } else {
        showError('Failed to save store settings.');
      }
    } catch (error) {
      showError('Failed to save store settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setSettings(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  useEffect(() => {
    if (authToken && selectedShopCode) {
      fetchSettings();
    }
  }, [authToken, selectedShopCode, fetchSettings]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        subtitle="Manage store details and application preferences"
        breadcrumbs={[{ label: 'Settings' }]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Col: Store Profile Form */}
        <div className="lg:col-span-2">
          <GlassmorphicCard
            title="Store Information"
            subtitle="Details displayed on customer invoices"
            action={
              !isEditing && !loading && (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="outline"
                  size="sm"
                >
                  Edit Information
                </Button>
              )
            }
          >
            {loading ? (
              <div className="py-12 text-center text-slate-400">
                <div className="inline-flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <span>Loading settings...</span>
                </div>
              </div>
            ) : (
              <form onSubmit={saveSettings} className="space-y-4">
                <InputField
                  label="Store Name"
                  value={settings.shop_name}
                  onChange={handleInputChange('shop_name')}
                  disabled={!isEditing}
                  required
                  placeholder="e.g. Amptess Mart (Ohwim)"
                  icon={<Store className="w-4 h-4" />}
                />

                <InputField
                  label="Address Line 1"
                  value={settings.shop_address_1}
                  onChange={handleInputChange('shop_address_1')}
                  disabled={!isEditing}
                  placeholder="Street address"
                  icon={<MapPin className="w-4 h-4" />}
                />

                <InputField
                  label="Address Line 2"
                  value={settings.shop_address_2}
                  onChange={handleInputChange('shop_address_2')}
                  disabled={!isEditing}
                  placeholder="City, Region"
                  icon={<MapPin className="w-4 h-4" />}
                />

                {isEditing && (
                  <div className="flex items-center gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <Button
                      type="submit"
                      disabled={saving}
                      icon={<Save className="w-4 h-4" />}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        fetchSettings();
                      }}
                      variant="light"
                      icon={<RotateCcw className="w-4 h-4" />}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </form>
            )}
          </GlassmorphicCard>
        </div>

        {/* Right Col: Maintenance */}
        <div>
          <GlassmorphicCard
            title="Session & Cache"
            subtitle="Application maintenance"
          >
            <div className="space-y-3">
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full justify-start text-xs"
                icon={<RefreshCw className="w-4 h-4" />}
              >
                Reload Application
              </Button>

              <Button
                onClick={() => {
                  if (window.confirm('Clear all local application cache and reload?')) {
                    localStorage.clear();
                    window.location.reload();
                  }
                }}
                variant="light"
                className="w-full justify-start text-xs text-rose-600 hover:text-rose-700"
                icon={<Trash2 className="w-4 h-4" />}
              >
                Clear Cache & Sign Out
              </Button>
            </div>
          </GlassmorphicCard>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;